import { CurrentSource, DesignSystems, TemplateVersion } from '@utils/shared';
import { createContext, useState, useContext } from 'react';
import { UserContext } from './UserProvider';
import { getGithubSessionStatus } from '@config/getGithubApis';

export interface ShareUser {
    label: string;
    value: string;
    email: string;
    isOwner: boolean;
}
export interface ModificationOptions {
    dataComponentFile: string;
    dataComponentStartLine: string;
    dataComponentName: string;
    componentType: string;
    elementContent: string;
    elementOuterHTML: string;
    elementTagName: string;
}

const useValue = () => {
    const { userEmail } = useContext(UserContext);
    const [isChatCollapsed, setIsChatCollapsed] = useState(false);
    const [sessionName, setSessionName] = useState('');
    const [isNewSession, setIsNewSession] = useState(true);
    const [isSandboxLoading, setIsSandboxLoading] = useState(false);
    const [isGenerateComplete, setIsGenerateComplete] = useState(true);
    const [firstPrompt, setFirstPrompt] = useState('');
    const [currentHistoryCommit, setCurrentHistoryCommit] = useState('');
    const [sessionOwnerEmail, setSessionOwnerEmail] = useState('');
    const isSessionOwner = sessionOwnerEmail !== '' && sessionOwnerEmail === userEmail;

    const [activeSessionTab, setActiveSessionTab] = useState(0);
    const [activeLayoutTab, setActiveLayoutTab] = useState(1);
    const [sortBy, setSortBy] = useState({ label: 'Last edited', value: 'lastEdited' });
    const [designSystemFilter, setDesignSystemFilter] = useState({ label: 'Select all', value: 'Select all' });
    const [attachFile, setAttachFile] = useState<File | null>(null);
    const [attachDataFile, setAttachDataFile] = useState<File | null>(null);
    const [processedImage, setProcessedImage] = useState<Blob | null>(null);
    const [processedDataToon, setProcessedDataToon] = useState<string | null>(null);
    const [processedDataFileName, setProcessedDataFileName] = useState<string | null>(null);
    const [isProcessingAttachment, setIsProcessingAttachment] = useState(false);
    const [attachmentProcessingError, setAttachmentProcessingError] = useState<string | null>(null);
    const [history, setHistory] = useState<any[]>([]);
    const [currentSource, setCurrentSource] = useState<CurrentSource>({});
    const [commitId, setCommitId] = useState('');
    const [thread, setThread] = useState<any[]>([]);
    const [sessionId, setSessionId] = useState('');
    const [designSystem, setDesignSystem] = useState<DesignSystems>('MaterialUI');
    const [templateVersion, setTemplateVersion] = useState<TemplateVersion>('v2');
    const [projectCount, setProjectCount] = useState(1);
    const [streamMessageProvider, setStreamMessageProvider] = useState<string[]>([]);
    const [streamMessagesConsumer, setStreamMessagesConsumer] = useState<string[]>([]);
    const [showCodeStream, setShowCodeStream] = useState(false);
    const [isTargetActive, setIsTargetActive] = useState(false);

    // const [showCodeEditor, setShowCodeEditor] = useState(false);
    const [devModeActive, setDevModeActive] = useState(false);
    const [modificationOptions, setModificationOptions] = useState<ModificationOptions>({
        componentType: '',
        dataComponentFile: '',
        dataComponentStartLine: '',
        dataComponentName: '',
        elementContent: '',
        elementOuterHTML: '',
        elementTagName: '',
    });
    const [sandpackErrors, setSandpackErrors] = useState<any[]>([]);
    // this is temp solution, as current streaming api does not return chatSessionId,
    // so need an extra state to get it from message history
    const [currentChatSessionId, setCurrentChatSessionId] = useState<number>(-1);
    const [sessionSelectedUsers, setSessionSelectedUsers] = useState<any[]>([]);

    const [currentGenerationId, setCurrentGenerationId] = useState<string>('');
    const [autoFixPrompt, setAutoFixPrompt] = useState<{
        prompt: string;
        attemptNumber: number;
        isRetryingOriginal: boolean;
    } | null>(null);

    const [generationError, setGenerationError] = useState<{
        message: string;
        error: any;
        prompt: string;
        timestamp: number;
    } | null>(null);

    const [toolStatus, setToolStatus] = useState<string | null>(null);
    const [generationStatus, setGenerationStatus] = useState<string | null>(null);
    const [generationSessionId, setGenerationSessionId] = useState<string>('');
    const [postProcessingFailed, setPostProcessingFailed] = useState<boolean>(false);

    // null = status not yet fetched (loading), true/false = known state
    const [githubLinked, setGithubLinked] = useState<boolean | null>(null);
    const [githubRepoUrl, setGithubRepoUrl] = useState('');
    const [githubBranch, setGithubBranch] = useState('design');
    const [githubLastPushedAt, setGithubLastPushedAt] = useState<string | null>(null);
    const [githubLastPushedSha, setGithubLastPushedSha] = useState<string | null>(null);
    const [githubLastPushStatus, setGithubLastPushStatus] = useState<string | null>(null);

    const refreshGithubSessionStatus = async (sid: string) => {
        // Reset to null (loading) at the start of every fresh fetch so the
        // menu never shows a stale label while the request is in-flight.
        setGithubLinked(null);
        try {
            const response = await getGithubSessionStatus(sid);
            if (response && response.code === 1 && response.resultObject) {
                const status = response.resultObject;
                setGithubLinked(status.linked ?? false);
                setGithubRepoUrl(status.repoUrl ?? '');
                setGithubBranch(status.branch ?? 'design');
                setGithubLastPushedAt(status.lastPushedAt ?? null);
                setGithubLastPushedSha(status.lastPushedSha ?? null);
                setGithubLastPushStatus(status.lastPushStatus ?? null);
            } else {
                setGithubLinked(false);
            }
        } catch (error: any) {
            if (error?.response?.status !== 401) {
                console.error('Error refreshing GitHub session status', error);
            }
            setGithubLinked(false);
        }
    };

    return {
        activeSessionTab,
        activeLayoutTab,
        attachFile,
        attachDataFile,
        processedImage,
        processedDataToon,
        processedDataFileName,
        isProcessingAttachment,
        attachmentProcessingError,
        currentSource,
        firstPrompt,
        isChatCollapsed,
        isNewSession,
        isSandboxLoading,
        isGenerateComplete,
        history,
        sessionName,
        sortBy,
        designSystemFilter,
        commitId,
        thread,
        sessionId,
        designSystem,
        templateVersion,
        projectCount,
        streamMessageProvider,
        streamMessagesConsumer,
        showCodeStream,
        devModeActive,
        modificationOptions,
        currentChatSessionId,
        sessionSelectedUsers,
        sandpackErrors,
        currentGenerationId,
        currentHistoryCommit,
        isTargetActive,
        sessionOwnerEmail,
        isSessionOwner,
        autoFixPrompt,
        generationError,
        toolStatus,
        generationStatus,
        generationSessionId,
        postProcessingFailed,
        setActiveLayoutTab,
        setActiveSessionTab,
        setAttachFile,
        setAttachDataFile,
        setProcessedImage,
        setProcessedDataToon,
        setProcessedDataFileName,
        setIsProcessingAttachment,
        setAttachmentProcessingError,
        setCurrentSource,
        setFirstPrompt,
        setIsChatCollapsed,
        setIsNewSession,
        setIsSandboxLoading,
        setIsGenerateComplete,
        setHistory,
        setSessionName,
        setSortBy,
        setDesignSystemFilter,
        setCommitId,
        setThread,
        setSessionId,
        setDesignSystem,
        setTemplateVersion,
        setProjectCount,
        setStreamMessageProvider,
        setStreamMessagesConsumer,
        setShowCodeStream,
        setDevModeActive,
        setModificationOptions,
        setCurrentChatSessionId,
        setSessionSelectedUsers,
        setSandpackErrors,
        setCurrentGenerationId,
        setCurrentHistoryCommit,
        setIsTargetActive,
        setSessionOwnerEmail,
        setAutoFixPrompt,
        setGenerationError,
        setToolStatus,
        setGenerationStatus,
        setGenerationSessionId,
        setPostProcessingFailed,
        githubLinked,
        githubRepoUrl,
        githubBranch,
        githubLastPushedAt,
        githubLastPushedSha,
        githubLastPushStatus,
        setGithubLinked,
        setGithubRepoUrl,
        setGithubBranch,
        setGithubLastPushedAt,
        setGithubLastPushedSha,
        setGithubLastPushStatus,
        refreshGithubSessionStatus,
    };
};

const SessionContext = createContext({} as ReturnType<typeof useValue>);

const SessionProvider = ({ children }: any) => {
    return <SessionContext.Provider value={useValue()}>{children}</SessionContext.Provider>;
};

export { SessionProvider, SessionContext };
